import React, { useState } from 'react';
import { ToggleSwitch, Modal, TextInput } from 'flowbite-react';
import { useCompanyStore } from '../../../state/useCompanyStore';
import { Button } from '../../common';

interface RestockAlertSettingsProps {
  settings: {
    restockAlert: {
      stock: number;
      channels: string[];
      enabled: boolean;
    };
  };
  onSettingsChange: (newSettings: any) => Promise<void>;
  showBanner: (message: string) => void;
}

export const RestockAlertSettings: React.FC<RestockAlertSettingsProps> = ({ 
  settings, 
  onSettingsChange,
  showBanner
}) => {
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockLevel, setStockLevel] = useState(settings.restockAlert.stock || 5);
  const [isLoading, setIsLoading] = useState(false); 

  const handleToggle = async (value: boolean) => {
    if (value) {
      setShowStockModal(true);
    } else {
      const updatedSettings = {
        ...settings,
        restockAlert: {
          ...settings.restockAlert,
          enabled: false
        }
      };
      await onSettingsChange(updatedSettings);
      showBanner('Low stock alert disabled successfully');
    }
  };

  const handleSaveStockLevel = async () => {
    setIsLoading(true); // Set loading state to true when saving
    try {
      const updatedSettings = {
        ...settings,
        restockAlert: {
          stock: stockLevel,
          channels: settings.restockAlert.channels || ['email'],
          enabled: true
        }
      };
      await onSettingsChange(updatedSettings);
      setShowStockModal(false);
      showBanner('Low stock alert settings saved successfully');
    } catch (error) {
      console.error('Error saving stock level:', error);
      showBanner('Failed to save stock alert settings');
    } finally {
      setIsLoading(false); // Reset loading state when done
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-4'>
        <h1 className='text-xl font-semibold'>Low Stock Alert</h1>
        <ToggleSwitch 
          checked={settings.restockAlert?.enabled || false} 
          onChange={handleToggle} 
        />
      </div>
      <p className=''>Configure how and when you receive alerts</p>
      <hr className='my-2' />

      {settings.restockAlert?.enabled && (
        <div className="py-4 rounded-lg">
          <div className="flex flex-col gap-2">
            <div>
              <p className="text-sm font-medium text-gray-500">Minimum stock level</p>
              <p className="text-lg font-semibold border w-full p-3 rounded-lg">{settings.restockAlert.stock}</p>
            </div>
            <div className='flex justify-end'>
              <p
                onClick={() => {
                  setStockLevel(settings.restockAlert.stock);
                  setShowStockModal(true);
                }}
                className='text-[#044E97] mt-4 font-semibold cursor-pointer'
              >
                Edit
              </p>
            </div>
          </div>
        </div>
      )}

      <Modal show={showStockModal} onClose={() => setShowStockModal(false)}>
        <Modal.Header>Enable Low Stock Alert</Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
              Low alert will be enabled once you click the save button
            </p>
            <div>
              <div className='flex items-center mb-2'>
                <label htmlFor="stock-level" className="block text-sm font-medium text-gray-900 dark:text-white">
                  Minimum Stock Level. 
                </label>
                <p className="text-sm text-gray-500 italic ml-1">Set the stock level that triggers the alert</p>
              </div>
              <TextInput
                id="stock-level"
                type="number"
                value={stockLevel}
                onChange={(e) => setStockLevel(Number(e.target.value))}
                min="1"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className='flex justify-end items-center'>
          <Button 
            size='small' 
            variant='white' 
            outline={true} 
            onClick={() => setShowStockModal(false)}
            disabled={isLoading} // Disable cancel button while loading
          >
            Cancel
          </Button>
          <Button 
            size='small' 
            variant='secondary' 
            onClick={handleSaveStockLevel}
            isLoading={isLoading} // Show loading spinner when saving
            disabled={isLoading} // Disable button while loading
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};